import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import './SessionDisplay.css';
import './SessionInvestigationDisplay.css';
import VideoDisplay from './VideoDisplay.jsx';
import MediaControls from './MediaControls.jsx';
import MediaIndicator from './MediaIndicator.jsx';
import AddressDisplay from './AddressDisplay.jsx';
import SessionControlPanel from './SessionControlPanel.jsx';
import DualCameraStreamPane from './DualCameraStreamPane.jsx';
import { useSessionLogic } from '../hooks/useSessionLogic.js';
import { journalText } from '../sessionJournal.js';

const clock = ms => new Date(ms).toLocaleTimeString('fr-FR');
const MIN_VIDEO_WIDTH = 220;
const MAX_VIDEO_SHARE = 0.65;
const MAX_DUAL_VIDEO_SHARE = 0.75;

function toggleFullscreen(element) {
    if (!element) return;
    if (document.fullscreenElement === element) document.exitFullscreen?.().catch(() => {});
    else element.requestFullscreen?.().catch(() => {});
}

const FullscreenButton = ({ target }) => (
    <button type="button" className="sid-fullscreen" title="Plein écran" aria-label="Plein écran"
        onClick={() => toggleFullscreen(target.current)}>⛶</button>
);

/**
 * Relecture d'une session (dashboard, lien police) : le moteur du direct
 * (timeline à double barre et segments de couleur, carte, redressement vidéo).
 * En haut, la vidéo (juste la largeur dont elle a besoin) et la carte (le
 * reste) ; en bas, la timeline à gauche et le journal à droite, qui suit
 * l'heure de lecture. Vidéo, carte et journal passent en plein écran.
 * L'accès est configuré par l'hôte (configureSessionAccess) avant le montage.
 */
export default function SessionInvestigationDisplay({ sessionId, apiUrl, header = null, toolbar = null, people = null, startAt = null }) {
    const [, setAlertDetails] = useState(null);
    const [address, setAddress] = useState('Recherche de l’adresse…');
    const [viewState, setViewState] = useState({ videoSection: false });
    const [videoWidth, setVideoWidth] = useState(null);
    const soundEnabledRef = useRef(false);
    const topRef = useRef(null);
    const mapSectionRef = useRef(null);
    const journalRef = useRef(null);
    const journalListRef = useRef(null);
    const refs = {
        video: useRef(null), canvas: useRef(null), canvasWrapper: useRef(null),
        mapElement: useRef(null), videoDisplaySection: useRef(null), audioMapDisplaySection: useRef(null),
        gpsMapDisplaySection: useRef(null), videoToggleButtonPlaceholder: useRef(null),
        audioToggleButtonPlaceholder: useRef(null), audioControlsToggleContainer: useRef(null),
        toggleViewButton: useRef(null), addressDisplayAudio: useRef(null), addressDisplayGps: useRef(null),
        mapContainerAudio: useRef(null), mapContainerGps: useRef(null),
        statusMessage: useRef(null),
    };
    const {
        timelineState, slaveState, rotationMode, toggleRotationMode, statusMessage, accessMessage,
        sessionState, isHistoricalMode, canGoLive, mediaHandlers, journal, timelineMarkers, invalidateMapSize,
        isDualCameraAvailable, mediaUrl, sessionMediaKey, backendUrl
    } = useSessionLogic({ refs, setAlertDetails, setAddress, setViewState, sessionId, apiUrl, layout: 'investigation' });
    const hasVideo = Boolean(viewState.videoSection);

    // Largeur fixe de la vidéo : celle qui l'affiche au plus grand à l'horizontale
    // (proportions paysage), quelle que soit la rotation du moment ; la carte prend le reste.
    useEffect(() => {
        if (!hasVideo) return undefined;
        const measure = () => {
            const video = refs.video.current;
            const top = topRef.current;
            if (!video?.videoWidth || !video?.videoHeight || !top) return;
            const ratio = Math.max(video.videoWidth, video.videoHeight) / Math.min(video.videoWidth, video.videoHeight);
            const maxShare = isDualCameraAvailable ? MAX_DUAL_VIDEO_SHARE : MAX_VIDEO_SHARE;
            const minWidth = isDualCameraAvailable ? MIN_VIDEO_WIDTH * 2 : MIN_VIDEO_WIDTH;
            const wanted = Math.round(top.clientHeight * ratio * (isDualCameraAvailable ? 1.6 : 1));
            const width = Math.max(minWidth, Math.min(wanted, Math.round(top.clientWidth * maxShare)));
            setVideoWidth(previous => (previous !== null && Math.abs(previous - width) < 4 ? previous : width));
        };
        measure();
        const video = refs.video.current;
        video?.addEventListener('loadedmetadata', measure);
        const observer = new ResizeObserver(measure);
        if (topRef.current) observer.observe(topRef.current);
        return () => { video?.removeEventListener('loadedmetadata', measure); observer.disconnect(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasVideo, isDualCameraAvailable]);

    // La carte suit la taille de son cadre (largeur de la vidéo, plein écran).
    useEffect(() => {
        const section = mapSectionRef.current;
        if (!section) return undefined;
        const observer = new ResizeObserver(() => invalidateMapSize?.());
        observer.observe(section);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Ouverture à un instant précis (alerte ouverte depuis la timeline d'un rendez-vous).
    const startAppliedRef = useRef(false);
    useEffect(() => {
        if (startAppliedRef.current || !Number.isFinite(startAt) || !Number.isFinite(timelineState.minTs)) return;
        startAppliedRef.current = true;
        mediaHandlers?.onTimelineChange?.(Math.max(0, (startAt - timelineState.minTs) / 1000));
    }, [startAt, timelineState.minTs, mediaHandlers]);

    // Journal au fil de la lecture : seules les lignes déjà atteintes.
    const currentTs = Number.isFinite(timelineState.minTs) ? timelineState.minTs + (timelineState.value || 0) * 1000 : null;
    const visible = useMemo(
        () => (currentTs === null ? [] : journal.filter(entry => entry.t <= currentTs)),
        [journal, currentTs],
    );
    useLayoutEffect(() => {
        const list = journalListRef.current;
        if (list) list.scrollTop = list.scrollHeight;
    }, [visible.length]);

    if (accessMessage) {
        return <div className="sid-root sid-message"><p>{accessMessage}</p></div>;
    }
    const mediaType = hasVideo ? 'video' : (viewState.audioControls ? 'audio' : 'gps');
    const seek = t => {
        if (!Number.isFinite(timelineState.minTs)) return;
        mediaHandlers?.onTimelineChange?.(Math.max(0, (t - timelineState.minTs) / 1000));
    };
    // La relecture démarre muette (règle d'autoplay) : le premier geste active le son.
    const enableSoundOnce = () => {
        if (soundEnabledRef.current) return;
        soundEnabledRef.current = true;
        mediaHandlers?.onEnableSound?.();
    };

    return (
        <div className="sid-root" onPointerDownCapture={enableSoundOnce}>
            <header className="sid-header">
                <div className="sid-header-left">{header}</div>
                <div className="sid-toolbar">{toolbar}</div>
            </header>

            <main ref={topRef} className={`sid-top ${isDualCameraAvailable ? 'sid-top-dual' : ''}`}
                style={isDualCameraAvailable ? undefined : { gridTemplateColumns: hasVideo ? `${videoWidth || MIN_VIDEO_WIDTH}px minmax(0, 1fr)` : 'minmax(0, 1fr)' }}>
                <section
                    className={`sid-video ${isDualCameraAvailable ? 'sid-video-dual' : ''}`}
                    style={{ display: hasVideo ? (isDualCameraAvailable ? 'grid' : 'block') : 'none' }}
                >
                    <div className={isDualCameraAvailable ? 'sid-video-pane' : ''} style={{ width: '100%', height: '100%' }}>
                        {isDualCameraAvailable && (
                            <div className="sid-dual-stream-label">Caméra Arrière</div>
                        )}
                        <VideoDisplay
                            refs={refs}
                            viewState={{ videoSection: true }}
                            timelineState={timelineState}
                            slaveState={slaveState}
                            mediaHandlers={mediaHandlers}
                            canGoLive={canGoLive}
                            statusMessage={statusMessage}
                            rotationMode={rotationMode}
                            toggleRotationMode={toggleRotationMode}
                            isDualCameraAvailable={isDualCameraAvailable}
                            embedded
                        />
                    </div>
                    {isDualCameraAvailable && (
                        <DualCameraStreamPane
                            streamUrl={mediaUrl?.('playlist_front.m3u8')}
                            sessionKey={sessionMediaKey}
                            backendUrl={backendUrl}
                            mainVideoElement={refs.video.current}
                            rotationMode={rotationMode}
                            label="Caméra Avant"
                        />
                    )}
                    <button type="button" className="sid-rotation" onClick={toggleRotationMode}>
                        {rotationMode === 'locked' ? 'Rotation libre' : 'Portrait/Paysage'}
                    </button>
                </section>

                <section ref={mapSectionRef} className="sid-map">
                    <div id="map-container-investigation" className="map-container" style={{ position: 'relative' }}>
                        <AddressDisplay addressRef={refs.addressDisplayGps} text={address} />
                        <SessionControlPanel sessionData={sessionState} isHistoricalMode={isHistoricalMode} />
                    </div>
                    <FullscreenButton target={mapSectionRef} />
                </section>
            </main>

            <div className="sid-bottom">
                <section className="sid-timeline">
                    <MediaControls
                        timelineState={timelineState}
                        onPlay={mediaHandlers?.onPlay}
                        onPause={mediaHandlers?.onPause}
                        onStart={mediaHandlers?.onStart}
                        onGoLive={mediaHandlers?.onGoLive}
                        canGoLive={canGoLive}
                        onTimelineInput={mediaHandlers?.onTimelineInput}
                        onTimelineChange={mediaHandlers?.onTimelineChange}
                        idPrefix="investigation-"
                        markers={timelineMarkers}
                    />
                    <MediaIndicator
                        timelineState={timelineState}
                        slaveState={slaveState}
                        onVolumeChange={mediaHandlers?.onVolumeChange}
                        mediaType={mediaType}
                    />
                </section>

                <section ref={journalRef} className="sid-journal" aria-label="Journal de la session">
                    <h2>Journal de la session <span>{visible.length}/{journal.length}</span></h2>
                    <FullscreenButton target={journalRef} />
                    {visible.length === 0 ? <p className="sid-empty">Aucun événement à cette heure.</p> : (
                        <ol ref={journalListRef}>
                            {visible.map((entry, index) => (
                                <li key={`${entry.t}-${index}`}>
                                    <button type="button" onClick={() => seek(entry.t)}>
                                        <span className="sid-dot" style={{ background: entry.color }} />
                                        <time>{clock(entry.t)}</time>
                                        <span>{journalText(entry, people)}</span>
                                    </button>
                                </li>
                            ))}
                        </ol>
                    )}
                </section>
            </div>

            {/* Carte déplacée par useSessionMap dans #map-container-investigation. */}
            <div id="map" ref={refs.mapElement} style={{ width: '100%', height: '100%', position: 'absolute', top: '-9999px', left: '0px' }} />
            <button id="toggle-view" ref={refs.toggleViewButton} style={{ display: 'none' }} />
        </div>
    );
}
